import React from 'react'
import * as yup from 'yup'
import { Formik, FormikHelpers } from 'formik'

// Icons
import { ReactComponent as MailIcon } from 'icons/mail-envelope-24.svg'

// Components
import { RoleDropdown } from '../RoleDropdown'

// Styles
import {
  InputWrapper,
  StyledButton,
  StyledForm,
  StyledFormField,
  StyledInput,
  Wrapper,
} from './MemberInvite.styles'

interface FormValues {
  email: string
  isEditor: boolean
  isAdmin: boolean
}
type FormActions = FormikHelpers<FormValues>

interface MemberInviteProps {
  onInvite: (member: FormValues) => Promise<void>
  isDisabled?: boolean
}

// Data
const INITIAL_VALUES = {
  email: '',
  isEditor: true,
  isAdmin: false,
}

// Formik Validation
const VALIDATION_SCHEMA = yup.object().shape({
  email: yup
    .string()
    .email('Must be a valid email address')
    .trim()
    .lowercase()
    .required('Email is a required field'),
  isEditor: yup.bool(),
  isAdmin: yup.bool(),
})

/**
 * MemberInvite
 *
 * Renders a text input, a dropdown and a invite button
 * that allows adding members to a workspace
 */
export const MemberInvite: React.FC<MemberInviteProps> = ({
  onInvite,
  isDisabled,
}) => {
  const handleSubmit = async (values: FormValues, actions: FormActions) => {
    /**
     *  Formik don't accept any changes from Yup, only validations
     *  that's why we need to enforce it when submitting
     */
    const trimmedValues = VALIDATION_SCHEMA.cast(values)

    try {
      await onInvite(trimmedValues)
    } catch (e) {
      actions.setFieldError('email', e.message)
      return
    }

    actions.setSubmitting(false)
    actions.resetForm({
      values: {
        email: '',
        isAdmin: false,
        isEditor: values.isEditor,
      },
    })
  }

  return (
    <Wrapper data-testid="member-invite">
      <Formik
        initialValues={INITIAL_VALUES}
        onSubmit={handleSubmit}
        validationSchema={VALIDATION_SCHEMA}>
        {({
          touched,
          values,
          errors,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          submitForm,
        }) => {
          const isFormDisabled = values?.email === '' || isDisabled
          const isLoading = isSubmitting

          return (
            <StyledForm>
              <InputWrapper>
                <StyledFormField
                  aria-label="Email"
                  name="email"
                  errorText={touched.email ? errors.email : undefined}>
                  <StyledInput
                    name="email"
                    type="text"
                    icon={<MailIcon width="24px" height="24px" />}
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isDisabled}
                    placeholder="Enter an email address"
                    small
                    onKeyDown={keyEvent => {
                      // Prevent Enter key submitting the whole form.
                      // see https://github.com/sketch-hq/Cloud/issues/8318
                      if (
                        keyEvent.key === 'Enter' ||
                        (keyEvent.charCode || keyEvent.keyCode) === 13
                      ) {
                        keyEvent.preventDefault()
                        submitForm()
                      }
                    }}
                  />
                </StyledFormField>
                <RoleDropdown
                  onEditor={() => {
                    setFieldValue('isEditor', true)
                  }}
                  onViewer={() => {
                    setFieldValue('isEditor', false)
                  }}
                  onToggleAdmin={() => {
                    setFieldValue('isAdmin', !values.isAdmin)
                  }}
                  isEditor={values.isEditor}
                  isAdmin={values.isAdmin}
                  isDisabled={isDisabled}
                  showAdminToggle
                  isEmbedded
                />
              </InputWrapper>
              <StyledButton
                type="button"
                variant="secondary-untinted"
                disabled={isFormDisabled}
                loading={isLoading}
                onClick={submitForm}
                small>
                Add to List
              </StyledButton>
            </StyledForm>
          )
        }}
      </Formik>
    </Wrapper>
  )
}
